import { Botao } from '../../../components/Botao'
import { TEXTOS } from '../../../lib/glossario'
import { dataSimples } from '../../../lib/datas'
import type { DisponibilidadeSondada as Sondagem } from '../../../types/chamado'

interface Props {
  sondagem: Sondagem
  /** Preenche os três campos de disponibilidade do formulário com o que a sonda mediu. */
  aoAdotar: (minutosDoPeriodo: number, minutosDeDowntime: number) => void
  jaAdotada: boolean
}

/**
 * O que a sonda da própria Prefeitura mediu — oferta ao fiscal, nunca apuração.
 *
 * **Por que isto existe.** Dos quatro sub-indicadores do IIE, três dependem de dado que só a
 * contratada tem: backup, restauração e comunicação de incidentes nascem nas ferramentas dela. O
 * quarto, não — disponibilidade é a única grandeza do ANEXO VIII que a CONTRATANTE consegue medir
 * com instrumento próprio, e ela já estava sendo medida sem que ninguém percebesse: a sonda de
 * observabilidade pergunta ao e-Cidade a cada dois minutos.
 *
 * Medir com instrumento próprio muda a posição da fiscalização numa contestação. Sem isto, quando a
 * contratada informa 99,9%, o fiscal não tem contra o que conferir.
 *
 * **Adotar é um clique, e é dele.** O botão preenche o formulário; quem registra continua sendo o
 * fiscal, e ele pode digitar outro número. É o mesmo desenho da criticidade do TIS — fonte
 * automática, decisão humana por cima.
 *
 * @param props a sondagem do mês e o que fazer ao adotá-la
 * @returns o cartão da sondagem
 */
export function DisponibilidadeSondada({ sondagem, aoAdotar, jaAdotada }: Props) {
  const t = TEXTOS.sondagem
  const percentual =
    sondagem.minutosDoPeriodo > 0
      ? (100 * (sondagem.minutosDoPeriodo - sondagem.minutosDeIndisponibilidade)) / sondagem.minutosDoPeriodo
      : 0

  return (
    <div className="rounded-lg border border-niteroi-200 bg-niteroi-50 p-4" aria-label={t.titulo}>
      <h3 className="text-sm font-semibold text-niteroi-900">{t.titulo}</h3>

      <p className="mt-2 text-2xl font-bold tabular-nums text-niteroi-900">
        {percentual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
      </p>
      <p className="text-sm text-slate-700">
        {t.fracao(sondagem.minutosDoPeriodo - sondagem.minutosDeIndisponibilidade, sondagem.minutosDoPeriodo)}
      </p>

      <dl className="mt-3 space-y-0.5 text-xs text-slate-600">
        <div className="flex gap-1">
          <dt>{t.medido}:</dt>
          <dd>
            {t.diasMedidos(sondagem.dias)} · {dataSimples(sondagem.primeiroDia)} a {dataSimples(sondagem.ultimoDia)}
          </dd>
        </div>
        {/* Os minutos observados ao lado dos minutos do mês, SEMPRE — e não só quando há ressalva.
            "99,9% · 31 dias · 01/08 a 31/08" é uma frase completa que esconde o buraco: os dias
            existem, as bordas batem, e ainda assim ~740 minutos daquele mês ninguém observou. A
            fração é o único lugar em que isso aparece antes de o fiscal clicar em "Usar estes
            números" e levar os minutos observados como denominador da medição. */}
        <div className="flex gap-1">
          <dt>{t.observado}:</dt>
          <dd className="tabular-nums">{t.minutosObservados(sondagem.minutosDoPeriodo, sondagem.minutosDoMes)}</dd>
        </div>
        <div className="flex gap-1">
          <dt>{t.alvo}:</dt>
          <dd className="break-all">{sondagem.alvo}</dd>
        </div>
        <div className="flex gap-1">
          <dt>{t.fonte}:</dt>
          <dd>{sondagem.fonte}</dd>
        </div>
      </dl>

      {!sondagem.cobreOMesInteiro && (
        <p className="mt-3 rounded-md bg-laranja-50 px-3 py-2 text-xs text-laranja-900">{t.mesIncompleto}</p>
      )}

      <p className="mt-3 max-w-prose text-xs text-slate-600">{t.ajuda}</p>

      <div className="mt-3">
        <Botao
          tamanho="pequeno"
          disabled={jaAdotada}
          onClick={() => aoAdotar(sondagem.minutosDoPeriodo, sondagem.minutosDeIndisponibilidade)}
        >
          {jaAdotada ? t.adotada : t.adotar}
        </Botao>
      </div>
    </div>
  )
}
